import React, {useEffect} from 'react';
import i18n, {availableLanguages} from './i18n';
import {useLanguageParam} from './useLanguageParam';
import Analytics from '../utils/Analytics';

// sessionStorage throws (SecurityError) when the browser blocks storage. This runs outside the route error boundary,
// so an uncaught throw blanks the whole page; without storage the language just doesn't survive a refresh.
const readStoredLang = (): string | null => {
  try {
    return sessionStorage.getItem('lang');
  } catch {
    return null;
  }
};

const storeLang = (lang: string) => {
  try {
    sessionStorage.setItem('lang', lang);
  } catch {
    // Storage blocked: the language still applies for this page.
  }
};

interface Props {
  children: React.ReactChildren | React.ReactChild;
}

/**
 * Wraps the app and handles the language change based on the `lang` param.
 * If a `lang` param is present it will set/update it. It sets the language
 * in the session storage and in the i18n config.
 * If the `lang` param is not present it will use the previously set language
 * or the default language set in the i18n config.
 *
 * This allows us to set the language via a query param, but not have to
 * plumb the param through on each request and allows the user to refresh the
 * page without losing their language setting.
 */
export const LocalizationHandler: React.FC<Props> = ({children}) => {
  let lang = useLanguageParam();

  if (lang) {
    lang = lang.toLowerCase();
  }

  useEffect(() => {
    const prevLang = readStoredLang();

    if (lang) {
      // If lang param is provided, use it
      if (availableLanguages.includes(lang) && (lang !== i18n.language || lang !== prevLang)) {
        storeLang(lang);
        i18n.changeLanguage(lang);
        Analytics.track('Language changed', {language: lang});
      }
    } else if (prevLang) {
      // if a previous language is stored, use that
      if (prevLang && prevLang !== i18n.language) {
        // Use previously stored language
        i18n.changeLanguage(prevLang);
        Analytics.track('Language changed', {language: prevLang});
      }
    } else {
      // If no lang param or saved language, try to use browser language
      const browserLang = navigator.language.split('-')[0]; // Get primary language code (e.g., 'pt' from 'pt-BR')

      if (availableLanguages.includes(browserLang) && browserLang !== i18n.language) {
        // Use browser language if it's supported
        i18n.changeLanguage(browserLang);
        Analytics.track('Language changed', {language: browserLang});
      }
    }
    // if nothing applies, we'll fall back to english
  }, [lang]);

  return <>{children}</>;
};
